/**
 * PageHead — reusable SEO component for landing, explore, business page.
 * Requires HelmetProvider in app root.
 *
 * Props:
 *   title — document title (can already include site name)
 *   description — meta description (truncated if needed)
 *   canonical — path or full URL (e.g. /explore, /business/my-slug, /en/blog?category=x)
 *   image — og:image / twitter:image URL
 *   type — og:type (default "website")
 *   noindex — set to true to add noindex
 *   jsonLd — one object or array of objects for application/ld+json (multiple script tags)
 *   articlePublishedTime / articleModifiedTime — ISO strings for article:* og tags when type=article
 *   robots — explicit robots content when indexable (e.g. max-image-preview for articles)
 *   rssAlternate — { href, title? } for <link rel="alternate" type="application/rss+xml">
 */
import { Helmet } from "react-helmet-async";
import {
  buildTitle,
  buildCanonical,
  truncateDescription,
  getSiteUrl,
  SITE_NAME,
} from "../../utils/seo";
import { useTranslation } from "../../i18n/I18nContext";
import { supportedLangs } from "../../i18n/loadTranslations";
import { usePageMetaOverride } from "../../hooks/usePageMetaOverride";

const OG_LOCALE_MAP = { en: "en_US", ru: "ru_RU", he: "he_IL" };
const HREFLANG_MAP = { en: "en", ru: "ru", he: "he" };

/** Split "/en/blog?cat=1" → { pathname: "/en/blog", search: "?cat=1" } */
function splitPathAndSearch(canonicalInput) {
  if (!canonicalInput || typeof canonicalInput !== "string") return { pathname: "", search: "" };
  if (canonicalInput.startsWith("http")) {
    try {
      const u = new URL(canonicalInput);
      return { pathname: u.pathname || "", search: u.search || "" };
    } catch {
      return { pathname: canonicalInput, search: "" };
    }
  }
  const q = canonicalInput.indexOf("?");
  if (q === -1) return { pathname: canonicalInput, search: "" };
  return {
    pathname: canonicalInput.slice(0, q),
    search: canonicalInput.slice(q),
  };
}

export function PageHead({
  title,
  description,
  canonical,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  articlePublishedTime,
  articleModifiedTime,
  /** When set and page is indexable, emitted as <meta name="robots" /> */
  robots,
  rssAlternate,
  /** Comma-separated or array of strings for <meta name="keywords" /> */
  keywords,
}) {
  const { lang } = useTranslation();
  const ogLocale = OG_LOCALE_MAP[lang] ?? "en_US";

  // Admin-authored overrides fetched from /api/public/seo/page-meta.
  // Any field present here wins over props; missing fields fall back to props.
  const override = usePageMetaOverride(canonical, lang);
  const effTitle = override?.title ?? title;
  const effDesc = override?.description ?? description;
  const effKeywords = override?.keywords ?? keywords;
  const effOgTitle = override?.og_title ?? null;
  const effOgDesc = override?.og_description ?? null;
  const effOgImage = override?.og_image ?? null;
  const effTwTitle = override?.twitter_title ?? null;
  const effTwDesc = override?.twitter_description ?? null;
  const effTwImage = override?.twitter_image ?? null;
  const effRobots = override?.robots ?? robots;
  const effCanonicalOverride = override?.canonical_override ?? null;

  const safeTitle = effTitle ? buildTitle(effTitle) : "PromBiz";
  const safeDesc = truncateDescription(effDesc || "", 160);
  const keywordsContent = Array.isArray(effKeywords)
    ? effKeywords.filter(Boolean).join(", ")
    : typeof effKeywords === "string"
      ? effKeywords.trim()
      : "";
  const canonicalSrc = effCanonicalOverride || canonical;
  const canonicalUrl = canonicalSrc
    ? (canonicalSrc.startsWith("http") ? canonicalSrc : buildCanonical(canonicalSrc))
    : null;

  const jsonLdBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // Build hreflang alternate URLs — swap locale prefix for each supported language (preserve query)
  const hreflangUrls = canonicalUrl
    ? supportedLangs.map((l) => {
        const { pathname, search } = splitPathAndSearch(canonical);
        const segments = pathname.split("/").filter(Boolean);
        const withoutLocale = supportedLangs.includes(segments[0])
          ? `/${segments.slice(1).join("/")}`
          : pathname.startsWith("/")
            ? pathname
            : `/${pathname}`;
        const pathForLocale =
          withoutLocale === "/" || withoutLocale === "" ? `/${l}` : `/${l}${withoutLocale}`;
        return { lang: HREFLANG_MAP[l], url: buildCanonical(`${pathForLocale}${search}`) };
      })
    : [];
  const effImage = effOgImage || image;
  const imageUrl = effImage
    ? (effImage.startsWith("http") ? effImage : buildCanonical(effImage.replace(/^\//, "")))
    : getSiteUrl() + "/logo512.png";
  const twitterImageUrl = effTwImage
    ? (effTwImage.startsWith("http") ? effTwImage : buildCanonical(effTwImage.replace(/^\//, "")))
    : imageUrl;

  return (
    <Helmet>
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      {keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : effRobots ? (
        <meta name="robots" content={effRobots} />
      ) : null}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {hreflangUrls.map(({ lang: l, url }) => (
        <link key={l} rel="alternate" hrefLang={l} href={url} />
      ))}
      {hreflangUrls.length > 0 && (
        <link rel="alternate" hrefLang="x-default" href={hreflangUrls.find((h) => h.lang === "en")?.url} />
      )}
      {rssAlternate?.href ? (
        <link
          rel="alternate"
          type="application/rss+xml"
          title={rssAlternate.title || `${SITE_NAME} Blog`}
          href={rssAlternate.href}
        />
      ) : null}
      <meta property="og:title" content={effOgTitle || safeTitle} />
      <meta property="og:description" content={effOgDesc || safeDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {supportedLangs
        .filter((l) => l !== lang)
        .map((l) => (
          <meta key={`og-alt-${l}`} property="og:locale:alternate" content={OG_LOCALE_MAP[l] ?? "en_US"} />
        ))}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={imageUrl} />
      {type === "article" && articlePublishedTime ? (
        <meta property="article:published_time" content={articlePublishedTime} />
      ) : null}
      {type === "article" && articleModifiedTime ? (
        <meta property="article:modified_time" content={articleModifiedTime} />
      ) : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={effTwTitle || safeTitle} />
      <meta name="twitter:description" content={effTwDesc || safeDesc} />
      <meta name="twitter:image" content={twitterImageUrl} />
      {jsonLdBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
